/* =============================================================
   Sweet Smiling Soul — Cart Upsell  ·  "Add a Pattern, 50% off"
                                      ·  Entry offers (crown / cushion)
   -------------------------------------------------------------
   Two placements, one script:
     1) Cart notification popup  ->  #cart-upsell
     2) Cart page (/cart)        ->  injected block

   TWO OFFERS, never both at once:

   A) MAIN OFFER — "add a pattern, 50% off"
      Shows when the cart already contains a book (Dollhouse /
      Adventure / Playtime) or the Bundle. The books not in the cart
      are offered at 50% off via a Buy X Get Y CODE.

   B) ENTRY OFFER — crown / cat cushion -> Playtime Storybook
      (Aug 2026) Shows when the cart has NO book anchor but DOES
      contain a free crown (Party or Christmas) or the $8.50 cat
      cushion. Offers the Playtime Storybook at its normal store
      price — its own compare-at price does the discounting, NO
      extra discount code is applied and nothing is stacked.
      Rationale: a $0 or $8.50 anchor can't be allowed to unlock a
      50%-off book, or the pattern ends up cheaper bought that way.
      Once Playtime is ticked, the cart has a real anchor and the
      MAIN offer takes over on the next render — Dollhouse and
      Adventure then appear at 50% off. That's the ladder.

   UX model (Sep 2026 update):
   - POPUP: the upsell sits BELOW the Checkout / View cart buttons, so the
     buttons never jump when it loads. Full copy + blurbs are kept.
     Ticking now adds the pattern to the cart IMMEDIATELY (same as the
     cart page) and applies the code, so the checkout total never holds
     a surprise. Checkout / View cart wait for any in-flight tick.
   - Product data is pre-fetched on page load and cached per currency in
     sessionStorage, so the upsell renders without the ~1s delay.

   UX model (Jul 2026, superseded for the popup):
   - POPUP: ticking is instant and local-only (light purple row, stored
     in sessionStorage). The ticked patterns are added and the codes
     applied (main offer only) in one go when the customer clicks
     "Checkout" or "View cart". Fail OPEN: checkout is never blocked.
   - CART PAGE: ticking updates the cart IMMEDIATELY. The row turns
     light purple instantly, then the item is added/removed, codes are
     applied where relevant, and the cart items + totals are refreshed
     in place via the Section Rendering API — no page reload. Any popup
     selections that weren't committed yet are committed on arrival.

   Discount (MAIN offer only): two generic Buy X Get Y CODES, applied
   ONLY for ticked add-ons (never store-wide). All books share the same
   price, so:
       1 add-on ticked  -> ADDPATTERN50OFF   (buy 1 pattern/bundle,
                                              get 1 pattern 50% off)
       2 add-ons ticked -> ADDPATTERN50OFFX2 (buy 1 pattern/bundle,
                                              get 2 patterns 50% off)
   BXGY needs a qualifying "buy" item, so the anchor can't be the
   discounted unit and a lone pattern can't be half-priced. Shopify
   picks which eligible line the 50% lands on — since all patterns cost
   the same, the total is identical either way.

   Codes are applied/removed with the Cart AJAX API:
       POST /cart/update.js  { discount: 'CODE1,CODE2' }   (Shopify, May 2025)
   NOTE: the discount param ADDS codes — syncDiscountCodes clears the
   set first, then applies exactly the desired codes. The ENTRY offer
   never calls it, so a code the customer typed themselves is never
   cleared by an entry tick.

   Add a 4th pattern later: add a row to PATTERNS below (with a short
   name + blurb), add the new product to BOTH discount codes' buy/get
   product lists in Shopify admin, and add it to the "Doll & Book
   Sewing Patterns" collection.

   Add another entry product later (another freebie, another cheap
   pattern): add its handle to ENTRY_ANCHORS with a `kind`, and give
   that kind a heading/blurb in ENTRY_COPY. No discount codes needed.
   ============================================================= */
class CartUpsell {
  constructor() {
    this.DISCOUNT_PCT = 0.5;

    /* inBundle: true  -> the pattern is included in the bundle, so it is
       never offered as an add-on when the bundle is in the cart.
       Playtime is NOT in the bundle, so it stays on offer for bundle
       buyers (its BXGY code accepts the bundle as the anchor). */
    this.PATTERNS = [
      { key: 'dollhouse', inBundle: true, name: '🏠 Dollhouse Storybook', handle: 'doll-fabric-dollhouse-sewing-pattern-beginner-pdf-pattern-diy-dollhouse-storybook-scrap-fabric-project-handmade-doll-pattern', blurb: 'Sewing pattern for a 14cm doll (girl and boy versions included) + a foldable fabric dollhouse book with 6 rooms to decorate: bedroom, bathroom, living room, dining room, hobby room, and wardrobe.' },
      { key: 'adventure', inBundle: true, name: '⛺️ Adventure Book', handle: 'diy-fabric-doll-and-adventure-book-sewing-pattern-beginner-friendly-pdf-pattern', blurb: 'Sewing pattern for a 14cm doll (girl and boy versions included) + a foldable fabric book with 7 outdoor adventure scenes: campsite, hammock, beach, treehouse and more.' },
      { key: 'playtime',  inBundle: false, name: '🧸 Playtime Storybook', handle: 'diy-fabric-doll-and-playtime-storybook-sewing-pattern-beginner-friendly-digital', blurb: 'Sewing pattern for a 14cm doll (girl and boy versions included) + a foldable fabric book with 8 playful scenes and fun tiny accessories: costume box, tea party, painting station, music room and more.' }
    ];
    this.BUNDLE_HANDLE = 'bundle-diy-fabric-doll-and-adventure-book-sewing-pattern-beginner-friendly-pdf-pattern';

    /* ---- ENTRY OFFER (no discount code, compare-at price only) ---- */
    this.ENTRY_ANCHORS = [
      /* NOTE: order matters. entryState() takes the kind of the FIRST
         match, so with both crowns in the cart the party-crown copy wins. */
      { handle: 'diy-fabric-party-crown-free-pdf-pattern', kind: 'crown_party' },
      { handle: 'diy-christmas-crown-free-pdf-pattern', kind: 'crown_xmas' },
      { handle: 'patchwork-cat-shaped-cushion-pdf-sewing-pattern-tutorial', kind: 'cushion' }
    ];
    this.ENTRY_TARGET_KEY = 'playtime';
    this.ENTRY_COPY = {
      crown_party: {
        noun: 'crown pattern',
        heading: 'Want to sew a doll that can wear a crown too?',
        sub: 'The Playtime Storybook includes the pattern for a little 14–15cm doll (girl and boy versions both included) with a tiny crown of their own to match, along with a cape, fairy wings, a wand and a whole costume box, across 8 playful scenes you can sew from the same fabric scraps.',
        rowBlurb: 'Sewing pattern for a 14–15cm doll (girl and boy versions included) + a foldable fabric book with 8 playful scenes and tiny accessories, including a little crown, cape and fairy wings for the doll to wear.'
      },
      crown_xmas: {
        noun: 'crown pattern',
        heading: 'Want to sew a Christmas gift to go with the crown?',
        sub: 'The Playtime Storybook includes the pattern for a little 14–15cm doll (girl and boy versions both included) with a tiny crown of their own to match, along with a cape, fairy wings, a wand and a whole costume box, across 8 playful scenes you can sew from the same fabric scraps.',
        rowBlurb: 'Sewing pattern for a 14–15cm doll (girl and boy versions included) + a foldable fabric book with 8 playful scenes and tiny accessories, including a little crown, cape and fairy wings for the doll to wear.'
      },
      cushion: {
        noun: 'cushion pattern',
        heading: 'Another scrap-friendly pattern to go with it',
        sub: 'The Playtime Storybook is also a great way to use up small fabric scraps. Use what you already have to create a little doll (girl and boy versions included) and a tiny world for them, with 8 playful scenes and small accessories to sew.',
        rowBlurb: 'Sewing pattern for a 14–15cm doll (girl and boy versions included) + a foldable fabric book with 8 playful scenes and fun tiny accessories: costume box, tea party, painting station, music room and more.'
      }
    };
    this.ENTRY_PROPERTY = 'Playtime add-on';

    // one generic customer-facing code per case (see header comment)
    this.SINGLE_CODE = 'ADDPATTERN50OFF';
    this.DOUBLE_CODE = 'ADDPATTERN50OFFX2';

    this.productCache = {};
    this.busy = false;
    this.debounceTimer = null;
    this._queue = Promise.resolve();
    this._cartCount = 0; // last known real cart item count (popup context)
    this._cartPageTimer = null;
    this._inflight = 0; // cart-page toggles currently being processed

    this.loadProductCache();
    this.injectStyles();
    this.observeNotification();
    this.warmProducts();
    if (this.isCartPage()) this.initCartPage();
  }

  /* ---------------- helpers ---------------- */
  isCartPage() {
    var p = window.location.pathname.replace(/\/+$/, '');
    return p === '/cart' || p.slice(-5) === '/cart' || !!document.querySelector('#main-cart-footer');
  }

  formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
  }

  cleanTitle(t) {
    return (t || '').replace(/^[^A-Za-z0-9]+/, '').replace(/^(NEW)\s*/i, '').trim();
  }

  getCart() { return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }); }

  /* product data cached per currency in sessionStorage, so the upsell
     renders instantly on every page after the first */
  cacheKey() {
    var c = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    return 'sss_products_' + c;
  }
  loadProductCache() {
    try { this.productCache = JSON.parse(sessionStorage.getItem(this.cacheKey()) || '{}') || {}; } catch (e) { this.productCache = {}; }
  }
  saveProductCache() {
    try { sessionStorage.setItem(this.cacheKey(), JSON.stringify(this.productCache)); } catch (e) {}
  }
  warmProducts() {
    var self = this;
    var run = function () {
      self.PATTERNS.forEach(function (p) { self.getProduct(p.handle); });
    };
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 1500 }); else setTimeout(run, 300);
  }

  getProduct(handle) {
    var self = this;
    if (this.productCache[handle]) return Promise.resolve(this.productCache[handle]);
    return fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var data = {
          handle: handle,
          title: p.title,
          variantId: p.variants[0].id,
          price: p.variants[0].price,
          compareAt: p.variants[0].compare_at_price || 0,
          image: p.featured_image || (p.images && p.images[0]) || ''
        };
        self.productCache[handle] = data;
        self.saveProductCache();
        return data;
      })
      .catch(function () { return null; });
  }

  patternByKey(k) { for (var i = 0; i < this.PATTERNS.length; i++) if (this.PATTERNS[i].key === k) return this.PATTERNS[i]; return null; }

  patternByHandle(handle) { for (var i = 0; i < this.PATTERNS.length; i++) if (this.PATTERNS[i].handle === handle) return this.PATTERNS[i]; return null; }

  patternByHandleKey(handle) {
    for (var i = 0; i < this.PATTERNS.length; i++) if (this.PATTERNS[i].handle === handle) return this.PATTERNS[i].key;
    return '';
  }

  patternsState(cart) {
    var handlesInCart = (cart.items || []).map(function (i) { return i.handle; });
    var inCart = [], missing = [];
    this.PATTERNS.forEach(function (pat) {
      if (handlesInCart.indexOf(pat.handle) !== -1) inCart.push(pat); else missing.push(pat);
    });
    return { inCart: inCart, missing: missing, bundleInCart: handlesInCart.indexOf(this.BUNDLE_HANDLE) !== -1 };
  }

  // true when the cart holds something that can anchor the 50% offer
  hasAnchor(state) { return state.bundleInCart || state.inCart.length >= 1; }

  // which patterns may be offered right now: all missing ones normally;
  // with the bundle in the cart, only patterns the bundle doesn't include
  offersFor(state) {
    if (state.bundleInCart) {
      return state.missing.filter(function (p) { return !p.inBundle; });
    }
    return state.missing;
  }

  /* ---- entry offer state: which freebie/cheap item is anchoring ---- */
  entryState(cart) {
    var handles = (cart.items || []).map(function (i) { return i.handle; });
    var kind = '';
    var anchors = [];
    this.ENTRY_ANCHORS.forEach(function (a) {
      if (handles.indexOf(a.handle) !== -1) {
        anchors.push(a.handle);
        if (!kind) kind = a.kind;
      }
    });
    var target = this.patternByKey(this.ENTRY_TARGET_KEY);
    return {
      kind: kind,
      anchors: anchors,
      target: target,
      targetInCart: target ? handles.indexOf(target.handle) !== -1 : true
    };
  }

  // the entry offer only runs when there is no 50%-off anchor at all
  showEntry(state, entry) {
    return !this.hasAnchor(state) && !!entry.kind && !entry.targetInCart && !!entry.target;
  }

  // serialize cart mutations so rapid ticks can't race each other
  enqueue(fn) {
    this._queue = this._queue.then(fn).catch(function () {});
    return this._queue;
  }

  /* ---- pending selections (popup only: ticked, not yet in the cart) ---- */
  getPending() { try { return JSON.parse(sessionStorage.getItem('sss_pending') || '[]'); } catch (e) { return []; } }
  setPending(a) { try { sessionStorage.setItem('sss_pending', JSON.stringify(a)); } catch (e) {} }
  addPending(key) { var a = this.getPending(); if (a.indexOf(key) === -1) { a.push(key); this.setPending(a); } }
  removePending(key) { this.setPending(this.getPending().filter(function (k) { return k !== key; })); }

  // keep pending in sync with reality: drop keys for patterns that are no
  // longer offered (already in cart, or the bundle covers them)
  prunePending(state) {
    var offerKeys = this.offersFor(state).map(function (p) { return p.key; });
    this.setPending(this.getPending().filter(function (k) { return offerKeys.indexOf(k) !== -1; }));
  }

  /* ---- entry pending (popup: ticked entry offer, not yet in the cart) ---- */
  getEntryPending() { try { return JSON.parse(sessionStorage.getItem('sss_entry_pending') || '[]'); } catch (e) { return []; } }
  setEntryPending(a) { try { sessionStorage.setItem('sss_entry_pending', JSON.stringify(a)); } catch (e) {} }
  addEntryPending(key) { var a = this.getEntryPending(); if (a.indexOf(key) === -1) { a.push(key); this.setEntryPending(a); } }
  removeEntryPending(key) { this.setEntryPending(this.getEntryPending().filter(function (k) { return k !== key; })); }
  pruneEntryPending(state, entry) { if (!this.showEntry(state, entry)) this.setEntryPending([]); }

  /* ---- committed add-ons (ticked AND added to the cart) ---- */
  getAddons() { try { return JSON.parse(sessionStorage.getItem('sss_addons') || '[]'); } catch (e) { return []; } }
  setAddons(a) { try { sessionStorage.setItem('sss_addons', JSON.stringify(a)); } catch (e) {} }
  getChannels() { try { return JSON.parse(sessionStorage.getItem('sss_addon_channels') || '{}'); } catch (e) { return {}; } }
  setChannels(m) { try { sessionStorage.setItem('sss_addon_channels', JSON.stringify(m)); } catch (e) {} }
  addAddon(key, channel) {
    var a = this.getAddons();
    if (a.indexOf(key) === -1) { a.push(key); this.setAddons(a); }
    var m = this.getChannels();
    m[key] = channel || m[key] || 'notification';
    this.setChannels(m);
  }
  removeAddon(key) {
    this.setAddons(this.getAddons().filter(function (k) { return k !== key; }));
    var m = this.getChannels();
    delete m[key];
    this.setChannels(m);
  }

  /* ---- committed entry add-ons (tracking only, no discount) ---- */
  getEntry() { try { return JSON.parse(sessionStorage.getItem('sss_entry') || '[]'); } catch (e) { return []; } }
  setEntry(a) { try { sessionStorage.setItem('sss_entry', JSON.stringify(a)); } catch (e) {} }
  getEntryChannels() { try { return JSON.parse(sessionStorage.getItem('sss_entry_channels') || '{}'); } catch (e) { return {}; } }
  setEntryChannels(m) { try { sessionStorage.setItem('sss_entry_channels', JSON.stringify(m)); } catch (e) {} }
  addEntry(key, channel, kind) {
    var a = this.getEntry();
    if (a.indexOf(key) === -1) { a.push(key); this.setEntry(a); }
    var m = this.getEntryChannels();
    m[key] = (channel || 'notification') + (kind ? '|' + kind : '');
    this.setEntryChannels(m);
  }
  removeEntry(key) {
    this.setEntry(this.getEntry().filter(function (k) { return k !== key; }));
    var m = this.getEntryChannels();
    delete m[key];
    this.setEntryChannels(m);
  }

  // one add-on -> the single code; two add-ons -> the x2 code
  codesForCommitted(committed) {
    if (committed.length >= 2) return [this.DOUBLE_CODE];
    if (committed.length === 1) return [this.SINGLE_CODE];
    return [];
  }

  // codes for committed add-ons that are STILL in the cart
  addonCodes(cart) {
    var handles = (cart.items || []).map(function (i) { return i.handle; });
    var self = this;
    var committed = this.getAddons()
      .map(function (k) { return self.patternByKey(k); })
      .filter(function (p) { return p && handles.indexOf(p.handle) !== -1; });
    return this.codesForCommitted(committed);
  }

  appliedSig() { try { return sessionStorage.getItem('sss_applied_sig') || ''; } catch (e) { return ''; } }
  setAppliedSig(s) { try { sessionStorage.setItem('sss_applied_sig', s); } catch (e) {} }

  postCart(payload) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); }).catch(function () { return null; });
  }

  /* ---- apply/remove discount codes + tracking attributes (one call) ----
     Cart attributes land on the order as note attributes ("Additional
     details" in admin) and are readable by Shopify Flow for tagging:
       pattern_upsell        -> "dollhouse:notification,playtime:cart_page"
       pattern_upsell_count  -> "1" / "2"
       pattern_upsell_anchor -> handles of the full-price items in the cart
                                (everything except the 50%-off add-ons, so an
                                entry-offer line counts as an anchor)
  */
  syncDiscountCodes() {
    var self = this;
    return this.getCart().then(function (cart) {
      var handles = (cart.items || []).map(function (i) { return i.handle; });
      var committed = self.getAddons()
        .map(function (k) { return self.patternByKey(k); })
        .filter(function (p) { return p && handles.indexOf(p.handle) !== -1; });

      // prune addon keys whose line item is gone, so stale codes never return
      self.setAddons(committed.map(function (p) { return p.key; }));

      var codes = self.codesForCommitted(committed);
      var channels = self.getChannels();
      var summary = committed.map(function (p) {
        return p.key + ':' + (channels[p.key] || 'notification');
      }).join(',');
      var anchors = (cart.items || [])
        /* only the 50%-off lines are excluded. An entry-offer line (Playtime
           added from a crown/cushion cart) was bought at full price and IS a
           valid BXGY anchor, so it belongs in this list. */
        .filter(function (i) { return !(i.properties && i.properties._addon); })
        .map(function (i) { return i.handle; })
        .join(',');

      self.setAppliedSig(codes.slice().sort().join(','));

      var attributes = {
        pattern_upsell: summary,
        pattern_upsell_count: committed.length ? String(committed.length) : '',
        pattern_upsell_anchor: committed.length ? anchors : ''
      };

      /* IMPORTANT: the `discount` param ADDS codes to the cart's existing
         set — it does not replace it. Stale codes (e.g. a single code left
         behind after switching to a pair code, or after removing a line)
         conflict with the new one and can block the 50% from applying.
         So: clear ALL codes first, then apply exactly the desired set. */
      var clearStep = self.postCart({ discount: '' });
      return clearStep.then(function () {
        return self.postCart({
          discount: codes.length ? codes.join(',') : '',
          attributes: attributes
        });
      }).then(function () { return cart; });
    });
  }

  /* ---- entry offer: tracking attributes ONLY, never touches discounts ----
       entry_upsell        -> "playtime:cart_page|crown"
       entry_upsell_anchor -> handles of the crown/cushion that anchored it
  */
  syncEntryAttributes() {
    var self = this;
    return this.getCart().then(function (cart) {
      var handles = (cart.items || []).map(function (i) { return i.handle; });
      var committed = self.getEntry()
        .map(function (k) { return self.patternByKey(k); })
        .filter(function (p) { return p && handles.indexOf(p.handle) !== -1; });

      self.setEntry(committed.map(function (p) { return p.key; }));

      var channels = self.getEntryChannels();
      var summary = committed.map(function (p) {
        return p.key + ':' + (channels[p.key] || 'notification');
      }).join(',');
      var anchors = self.ENTRY_ANCHORS
        .filter(function (a) { return handles.indexOf(a.handle) !== -1; })
        .map(function (a) { return a.handle; })
        .join(',');

      return self.postCart({
        attributes: {
          entry_upsell: summary,
          entry_upsell_anchor: committed.length ? anchors : ''
        }
      }).then(function () { return cart; });
    });
  }

  refreshCartCount() {
    this.getCart().then(function (cart) {
      document.querySelectorAll('.cart-count-bubble').forEach(function (b) {
        b.querySelectorAll('span').forEach(function (s) {
          if (!s.classList.contains('visually-hidden')) s.textContent = cart.item_count;
        });
      });
    });
  }

  /* ---- refresh cart page items + totals in place (no reload) ---- */
  refreshCartSections() {
    var itemsEl = document.getElementById('main-cart-items');
    var footerEl = document.getElementById('main-cart-footer');
    var itemsId = itemsEl ? itemsEl.dataset.id : null;
    var footerId = footerEl ? footerEl.dataset.id : null;
    var ids = [];
    if (itemsId) ids.push(itemsId);
    if (footerId && footerId !== itemsId) ids.push(footerId);
    if (!ids.length) { window.location.reload(); return Promise.resolve(); }

    return fetch(window.location.pathname + '?sections=' + ids.join(','))
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var parser = new DOMParser();
        [[itemsId, '#main-cart-items'], [footerId, '#main-cart-footer']].forEach(function (pair) {
          var id = pair[0];
          if (!id || !sections[id]) return;
          var doc = parser.parseFromString(sections[id], 'text/html');
          var source = doc.querySelector(pair[1] + ' .js-contents') || doc.querySelector('.js-contents');
          var target = document.querySelector(pair[1] + ' .js-contents');
          if (source && target) target.innerHTML = source.innerHTML;
        });
      })
      .catch(function () { window.location.reload(); });
  }

  /* ---------------- commit pending (popup CTAs) ---------------- */
  lockCta(el, label) {
    if (!el) return;
    el.setAttribute('aria-busy', 'true');
    el.style.pointerEvents = 'none';
    el.style.opacity = '.7';
    if (el.tagName === 'BUTTON') el.disabled = true;
    if (label) el.textContent = label;
  }

  // add the given patterns (that aren't in the cart yet) in one call
  addPatterns(patterns, cart, isEntry) {
    var self = this;
    var handles = (cart.items || []).map(function (i) { return i.handle; });
    var toAdd = patterns.filter(function (p) { return p && handles.indexOf(p.handle) === -1; });
    if (!toAdd.length) return Promise.resolve(null);
    return Promise.all(toAdd.map(function (p) { return self.getProduct(p.handle); })).then(function (prods) {
      var items = prods.filter(Boolean).map(function (p) {
        return { id: p.variantId, quantity: 1, properties: self.lineProperties(isEntry) };
      });
      if (!items.length) return null;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) { return r.json(); });
    });
  }

  lineProperties(isEntry) {
    return isEntry
      ? { _entry_addon: this.ENTRY_PROPERTY }
      : { _addon: 'Add-a-pattern 50% off' };
  }

  hasPendingWork() { return this.getPending().length > 0 || this.getEntryPending().length > 0; }

  commitPending(nextUrl, ctaEl) {
    var self = this;
    if (this.busy) return;
    this.busy = true;
    this.lockCta(ctaEl, 'Adding your pattern…');

    var go = function () { window.location.href = nextUrl; };

    this.getCart().then(function (cart) {
      var pend = self.getPending().map(function (k) { return self.patternByKey(k); }).filter(Boolean);
      var entryPend = self.getEntryPending().map(function (k) { return self.patternByKey(k); }).filter(Boolean);
      var entryKind = self.entryState(cart).kind;

      return self.addPatterns(pend, cart, false)
        .then(function () { return self.addPatterns(entryPend, cart, true); })
        .then(function () {
          pend.forEach(function (p) { self.addAddon(p.key, 'notification'); });
          entryPend.forEach(function (p) { self.addEntry(p.key, 'notification', entryKind); });
          self.setPending([]);
          self.setEntryPending([]);
          // codes only ever come from the 50% offer
          var step = pend.length ? self.syncDiscountCodes() : Promise.resolve(null);
          return step.then(function () {
            return entryPend.length ? self.syncEntryAttributes() : null;
          });
        });
    }).then(go).catch(go); // fail open — never block checkout
  }

  /* ---------------- shared markup ---------------- */
  priceMarkup(full, now, badge) {
    return '<span class="sss-bump__price">' +
      (full ? '<s>' + full + '</s> ' : '') +
      '<strong>' + now + '</strong>' +
      (badge ? ' <em>' + badge + '</em>' : '') +
      '</span>';
  }

  bumpRow(opts) {
    var img = opts.image ? (opts.image + (opts.image.indexOf('?') > -1 ? '&' : '?') + 'width=160') : '';
    return (
      '<label class="sss-bump' + (opts.selected ? ' is-selected' : '') + '" data-key="' + opts.key + '">' +
        '<input type="checkbox" class="sss-bump__check" data-key="' + opts.key + '"' +
          (opts.mode ? ' data-mode="' + opts.mode + '"' : '') +
          (opts.selected ? ' checked' : '') + '>' +
        (img ? '<img class="sss-bump__img" src="' + img + '" alt="" width="64" height="64" loading="lazy">' : '') +
        '<span class="sss-bump__text">' +
          '<span class="sss-bump__title">' + opts.title + '</span>' +
          (opts.blurb ? '<span class="sss-bump__blurb">' + opts.blurb + '</span>' : '') +
          opts.price +
        '</span>' +
      '</label>'
    );
  }

  upsellMarkup(products, context, bundleInCart) {
    var self = this;
    var pending = this.getPending();
    var rows = products.map(function (p) {
      var full = self.formatMoney(p.price);
      var half = self.formatMoney(Math.round(p.price * self.DISCOUNT_PCT));
      var pat  = self.patternByHandle(p.handle);
      var blurb = pat && pat.blurb ? pat.blurb : '';
      var title = pat && pat.name ? pat.name : self.cleanTitle(p.title);
      var key = self.patternByHandleKey(p.handle);
      return self.bumpRow({
        key: key,
        mode: '',
        selected: context === 'notification' && pending.indexOf(key) !== -1,
        image: p.image,
        title: 'Yes! Add the ' + title,
        blurb: blurb,
        price: self.priceMarkup(full, half, '50% off')
      });
    }).join('');

    var heading;
    if (bundleInCart) {
      heading = 'Complete the collection: add the Playtime Storybook for 50% off';
    } else if (products.length > 1) {
      heading = 'Complete your set &amp; save 50%';
    } else {
      heading = 'Add the matching pattern for 50% off';
    }

    /* Sep 2026: ticking adds instantly in BOTH the popup and the cart page */
    var note = 'Ticked patterns are added to your cart instantly at 50% off. No code needed. Offer is not available anywhere else.';

    return (
      '<div class="sss-upsell">' +
        '<p class="sss-upsell__head">★ ' + heading + '</p>' +
        '<p class="sss-upsell__sub">These patterns are made to go together. The same little 14cm doll plays across every book, so you can build a whole world for them to explore. Tick to add another beginner-friendly pattern at <strong>half price</strong>. Every pattern is an instant PDF download with free step-by-step written, photo, and video tutorials, made to sew from felt and fabric scraps you already have.</p>' +
        rows +
        '<p class="sss-bump__note">' + note + '</p>' +
      '</div>'
    );
  }

  /* entry offer markup: normal store price, compare-at does the work */
  entryMarkup(product, kind, context) {
    var copy = this.ENTRY_COPY[kind] || this.ENTRY_COPY.crown_party;
    var pat = this.patternByHandle(product.handle);
    var title = pat && pat.name ? pat.name : this.cleanTitle(product.title);
    var now = this.formatMoney(product.price);
    var full = product.compareAt && product.compareAt > product.price ? this.formatMoney(product.compareAt) : '';
    var badge = product.compareAt && product.compareAt > product.price
      ? 'Save ' + this.formatMoney(product.compareAt - product.price)
      : '';

    var row = this.bumpRow({
      key: pat ? pat.key : this.ENTRY_TARGET_KEY,
      mode: 'entry',
      selected: context === 'notification' && this.getEntryPending().indexOf(this.ENTRY_TARGET_KEY) !== -1,
      image: product.image,
      title: 'Yes! Add the ' + title,
      blurb: copy.rowBlurb,
      price: this.priceMarkup(full, now, badge)
    });

    var noun = copy.noun || 'pattern';
    var note = 'Ticked patterns are added to your cart instantly. Instant PDF download, just like your ' + noun + '. Nothing is posted.';

    return (
      '<div class="sss-upsell">' +
        '<p class="sss-upsell__head">★ ' + copy.heading + '</p>' +
        '<p class="sss-upsell__sub">' + copy.sub + '</p>' +
        row +
        '<p class="sss-bump__note">' + note + '</p>' +
      '</div>'
    );
  }

  injectStyles() {
    if (document.getElementById('sss-upsell-styles')) return;
    var css =
      /* --- popup: wider on tablet/desktop, one scroll column, CTA always visible --- */
      '@media screen and (min-width:750px){.cart-notification{max-width:46rem !important;}}' +
      '#cart-notification{max-height:calc(100vh - 70px);max-height:calc(100dvh - 70px);overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;}' +
      '.cart-notification-product{padding-top:1.2rem !important;padding-bottom:1.2rem !important;}' +
      /* sticky primary CTA inside the scrolling popup */
      '.sss-cta-sticky{position:sticky;bottom:0;z-index:2;background:rgb(var(--color-background));margin:0 -3.5rem;padding:.8rem 3.5rem calc(.8rem + env(safe-area-inset-bottom,0px));box-shadow:0 -6px 12px -8px rgba(0,0,0,.18);}' +
      '.sss-trust{display:block;margin:.7rem 0 0;font-size:1.05rem;line-height:1.4;color:rgba(var(--color-foreground),.7);text-align:center;}' +
      /* upsell block: no nested scrolling — the popup itself scrolls */
      '#cart-upsell{display:block;margin:0 0 1rem;}' +
      '#main-cart-items .cart-upsell{display:none !important;}' +
      '.sss-upsell-wrap{margin:2rem 0 .5rem;}' +
      '.sss-upsell-wrap .sss-upsell{max-width:62rem;margin-left:auto;margin-right:auto;}' +
      '.sss-upsell{border:1px solid #f0d9e2;background:#fff7fb;border-radius:12px;padding:14px 16px;margin:0 0 16px;text-align:left;}' +
      '.sss-upsell__head{margin:0 0 4px;font-weight:700;color:#46523e;font-size:1.45rem;}' +
      '.sss-upsell__sub{margin:0 0 12px;color:#6b7363;font-size:1.15rem;line-height:1.45;}' +
      '@media screen and (max-width:479px){.sss-upsell{padding:12px;}.sss-upsell__sub{margin-bottom:10px;}}' +
      '.sss-bump{display:flex;align-items:flex-start;gap:12px;border:1px solid #e8dfe4;background:#fff;border-radius:10px;padding:12px;margin-bottom:10px;cursor:pointer;transition:border-color .15s,background .15s,box-shadow .15s;}' +
      '.sss-bump:last-of-type{margin-bottom:0;}' +
      '@media (hover:hover){.sss-bump:hover{border-color:#c9a3d8;box-shadow:0 1px 4px rgba(181,143,214,.25);}}' +
      /* selected = light purple, instantly, and it stays that way */
      '.sss-bump.is-selected{border-color:#b58fd6;background:#f6effb;}' +
      '.sss-bump input{width:22px;height:22px;accent-color:#a678c9;flex:none;cursor:pointer;margin:2px 0 0;}' +
      '.sss-bump__img{border-radius:8px;object-fit:cover;flex:none;width:64px;height:64px;}' +
      '@media screen and (max-width:479px){.sss-bump__img{width:56px;height:56px;}}' +
      '.sss-bump__text{display:flex;flex-direction:column;gap:3px;min-width:0;}' +
      '.sss-bump__title{font-weight:600;color:#46523e;font-size:1.25rem;line-height:1.3;}' +
      '.sss-bump__blurb{font-size:1.12rem;color:#7a7f72;line-height:1.4;}' +
      '.sss-bump__price{font-size:1.2rem;color:#6b7363;margin-top:1px;}' +
      '.sss-bump__price s{color:#b9a0a8;margin-right:5px;}' +
      '.sss-bump__price strong{color:#2d5a2d;font-size:1.35rem;}' +
      '.sss-bump__price em{display:inline-block;background:#2d5a2d;color:#fff;font-style:normal;font-size:1.0rem;font-weight:700;padding:1px 8px;border-radius:20px;margin-left:5px;vertical-align:middle;}' +
      '.sss-bump__note{margin:8px 0 0;font-size:1.05rem;color:#8a8f82;}' +
      /* --- Sep 2026: upsell under / beside the buttons --- */
      '.cart-notification__links #cart-upsell{margin:1.6rem 0 .4rem;}' +
      /* cart page checkout: solid, clearly clickable (was pale pink) */
      '#checkout.cart__checkout-button{--color-button:18,18,18;--color-button-text:255,255,255;}' +
      /* accepted payment icons under the checkout buttons */
      '.sss-pay{display:flex;flex-wrap:wrap;justify-content:center;gap:6px;margin:.8rem 0 0;padding:0;}' +
      '.sss-pay__item{display:flex;line-height:0;}' +
      '.sss-pay .icon{width:3.4rem;height:2.2rem;}' +
      '.cart__blocks .sss-upsell-wrap{margin:2.4rem 0 0;text-align:left;}' +
      '.cart__blocks .sss-upsell-wrap .sss-upsell{max-width:none;margin:0;}' +
      '.cart__footer > .sss-upsell-wrap{margin:0;text-align:left;}' +
      '.cart__footer > .sss-upsell-wrap .sss-upsell{max-width:none;margin:0;}' +
      '@media screen and (min-width:750px){.cart__footer > .sss-upsell-wrap{width:auto;flex:0 1 52rem;margin-right:4rem;align-self:flex-start;}}';
    var style = document.createElement('style');
    style.id = 'sss-upsell-styles';
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
  }

  bindBumps(scope, context) {
    var self = this;
    scope.querySelectorAll('.sss-bump__check').forEach(function (input) {
      input.addEventListener('change', function () {
        var key = input.dataset.key;
        var isEntry = input.dataset.mode === 'entry';
        var row = input.closest('.sss-bump');
        var checked = input.checked;
        // instant visual state, both contexts
        if (row) row.classList.toggle('is-selected', checked);

        // both contexts: update the cart right away (queued to avoid races)
        self._inflight++;
        self.enqueue(function () {
          var op = isEntry
            ? self.applyEntryToggle(key, checked, input, row, context)
            : self.applyCartPageToggle(key, checked, input, row, context);
          return op.then(function () { self._inflight--; }, function () { self._inflight--; });
        });
      });
    });
  }

  // cart page: add/remove the pattern, sync codes, refresh totals in place
  applyCartPageToggle(key, checked, input, row, context) {
    var self = this;
    var channel = context === 'notification' ? 'notification' : 'cart_page';
    var pat = this.patternByKey(key);
    if (!pat) return Promise.resolve();

    return this.getProduct(pat.handle).then(function (p) {
      if (!p) throw new Error('product unavailable');
      return checked
        ? fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: p.variantId, quantity: 1, properties: self.lineProperties(false) }] }) })
        : fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: p.variantId, quantity: 0 }) });
    }).then(function (r) { return r.json(); }).then(function () {
      if (checked) self.addAddon(key, channel); else self.removeAddon(key);
      return self.syncDiscountCodes();
    }).then(function () {
      return self.afterToggle(context);
    }).catch(function () {
      // revert the checkbox if anything failed
      input.checked = !checked;
      if (row) row.classList.toggle('is-selected', !checked);
    });
  }

  /* cart page, ENTRY offer: add/remove at normal price. No discount code
     is applied or cleared — a code the customer entered themselves stays
     exactly where it is. */
  applyEntryToggle(key, checked, input, row, context) {
    var self = this;
    var channel = context === 'notification' ? 'notification' : 'cart_page';
    var pat = this.patternByKey(key);
    if (!pat) return Promise.resolve();
    var kind = '';

    return this.getCart().then(function (cart) {
      kind = self.entryState(cart).kind;
      return self.getProduct(pat.handle);
    }).then(function (p) {
      if (!p) throw new Error('product unavailable');
      return checked
        ? fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: p.variantId, quantity: 1, properties: self.lineProperties(true) }] }) })
        : fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: p.variantId, quantity: 0 }) });
    }).then(function (r) { return r.json(); }).then(function () {
      if (checked) self.addEntry(key, channel, kind); else self.removeEntry(key);
      return self.syncEntryAttributes();
    }).then(function () {
      return self.afterToggle(context);
    }).catch(function () {
      input.checked = !checked;
      if (row) row.classList.toggle('is-selected', !checked);
    });
  }

  /* after a tick: cart page re-renders items + totals; popup just updates
     the header bubble and the "View cart (N)" label */
  afterToggle(context) {
    var self = this;
    if (context !== 'notification') {
      return this.refreshCartSections().then(function () { self.refreshCartCount(); });
    }
    return this.getCart().then(function (cart) {
      self._cartCount = cart.item_count || 0;
      self.updateViewCartCount();
      document.querySelectorAll('.cart-count-bubble').forEach(function (b) {
        b.querySelectorAll('span').forEach(function (s) {
          if (!s.classList.contains('visually-hidden')) s.textContent = cart.item_count;
        });
      });
    });
  }

  /* ---------------- placement 1: notification popup ---------------- */
  observeNotification() {
    var target = document.querySelector('cart-notification') || document.getElementById('CartNotification');
    if (!target) return;
    var self = this;
    new MutationObserver(function (records) {
      /* ignore mutations we caused ourselves (upsell block, View cart
         label). Otherwise ticking a row (which now adds it to the cart)
         would re-render the block and make the ticked row vanish. */
      var external = records.some(function (r) {
        var n = r.target.nodeType === 1 ? r.target : r.target.parentElement;
        return n && !n.closest('#cart-upsell') && !n.closest('#cart-notification-button');
      });
      if (!external) return;
      clearTimeout(self.debounceTimer);
      self.debounceTimer = setTimeout(function () { self.renderNotification(); }, 30);
    }).observe(target, { childList: true, subtree: true, characterData: true });
  }

  renderNotification() {
    var container = document.getElementById('cart-upsell');
    if (!container) return;
    var self = this;
    var clear = function () { container.innerHTML = ''; container.dataset.sig = ''; };

    this.getCart().then(function (cart) {
      if (!cart.items || !cart.items.length) { clear(); return; }
      var state = self.patternsState(cart);
      var entry = self.entryState(cart);
      self.prunePending(state);
      self.pruneEntryPending(state, entry);
      self._cartCount = cart.item_count || 0;
      self.updateViewCartCount();

      var offers = self.offersFor(state);

      /* A) main offer — a book or the bundle is already in the cart */
      if (self.hasAnchor(state) && offers.length >= 1) {
        var sig = 'n:' + offers.map(function (p) { return p.key; }).join(',');
        if (container.dataset.sig === sig) return;
        container.dataset.sig = sig;

        Promise.all(offers.map(function (p) { return self.getProduct(p.handle); })).then(function (prods) {
          prods = prods.filter(Boolean);
          if (!prods.length) { container.dataset.sig = ''; return; }
          container.innerHTML = self.upsellMarkup(prods, 'notification', state.bundleInCart);
          self.bindBumps(container, 'notification');
          self.bindNotificationCommit();
        });
        return;
      }

      /* B) entry offer — only a crown / cushion in the cart */
      if (self.showEntry(state, entry)) {
        var esig = 'ne:' + entry.kind;
        if (container.dataset.sig === esig) return;
        container.dataset.sig = esig;

        self.getProduct(entry.target.handle).then(function (p) {
          if (!p) { container.dataset.sig = ''; return; }
          container.innerHTML = self.entryMarkup(p, entry.kind, 'notification');
          self.bindBumps(container, 'notification');
          self.bindNotificationCommit();
        });
        return;
      }

      clear();
    });
  }

  /* keep the "View cart (N)" label = real cart items + pending ticks.
     Only writes when the text actually changes, so the MutationObserver
     on the popup can't loop. */
  updateViewCartCount() {
    var btn = document.getElementById('cart-notification-button');
    if (!btn) return;
    var total = (this._cartCount || 0) + this.getPending().length + this.getEntryPending().length;
    if (!total) return;
    var current = btn.textContent;
    var next = /\(\d+\)/.test(current)
      ? current.replace(/\(\d+\)/, '(' + total + ')')
      : current.trim() + ' (' + total + ')';
    if (current !== next) btn.textContent = next;
  }

  bindNotificationCommit() {
    var self = this;

    // Checkout: add pending patterns + codes first, then continue to checkout
    var form = document.getElementById('cart-notification-form');
    if (form && !form.dataset.sssBound) {
      form.dataset.sssBound = '1';
      form.addEventListener('submit', function (e) {
        if (self._inflight > 0) {
          e.preventDefault();
          self.lockCta(form.querySelector('button'), 'One moment…');
          self._queue.then(function () { window.location.href = '/checkout'; });
          return;
        }
        if (!self.hasPendingWork()) return; // nothing pending — normal checkout
        e.preventDefault();
        self.commitPending('/checkout', form.querySelector('button'));
      });
    }

    // View cart: same, then continue to the cart page
    var viewCart = document.getElementById('cart-notification-button');
    if (viewCart && !viewCart.dataset.sssBound) {
      viewCart.dataset.sssBound = '1';
      viewCart.addEventListener('click', function (e) {
        if (self._inflight > 0) {
          e.preventDefault();
          self._queue.then(function () { window.location.href = '/cart'; });
          return;
        }
        if (!self.hasPendingWork()) return; // nothing pending — normal link
        e.preventDefault();
        self.commitPending('/cart', viewCart);
      });
    }
  }

  /* if the customer ticks a box and clicks Checkout before the add +
     discount calls finish, hold the navigation until they complete —
     otherwise the discount update is cancelled mid-flight */
  bindCartPageGuard() {
    var self = this;
    var btn = document.getElementById('checkout');
    if (!btn || btn.dataset.sssGuard) return;
    btn.dataset.sssGuard = '1';
    btn.addEventListener('click', function (e) {
      if (self._inflight === 0) return; // nothing in flight — normal checkout
      e.preventDefault();
      self.lockCta(btn, 'One moment…');
      self._queue.then(function () { window.location.href = '/checkout'; });
    });
  }

  /* ---------------- placement 2: cart page ---------------- */
  initCartPage() {
    var self = this;
    this.observeCartPage();
    this.bindCartPageGuard();
    this.getCart().then(function (cart) {
      if (!cart.items || !cart.items.length) return;
      var state = self.patternsState(cart);
      var entry = self.entryState(cart);
      self.prunePending(state);
      self.pruneEntryPending(state, entry);

      // commit any selections ticked in the popup but not committed yet
      // (e.g. the customer used the header cart icon instead of "View cart")
      var pend = self.getPending().map(function (k) { return self.patternByKey(k); }).filter(Boolean);
      var entryPend = self.getEntryPending().map(function (k) { return self.patternByKey(k); }).filter(Boolean);
      var entryKind = entry.kind;

      var commitStep = (pend.length || entryPend.length)
        ? self.addPatterns(pend, cart, false)
            .then(function () { return self.addPatterns(entryPend, cart, true); })
            .then(function () {
              pend.forEach(function (p) { self.addAddon(p.key, 'notification'); });
              entryPend.forEach(function (p) { self.addEntry(p.key, 'notification', entryKind); });
              self.setPending([]);
              self.setEntryPending([]);
              return entryPend.length ? self.syncEntryAttributes() : null;
            })
        : Promise.resolve();

      commitStep.then(function () { return self.getCart(); }).then(function (cart2) {
        var codes = self.addonCodes(cart2);
        var sig = codes.slice().sort().join(',');
        var needSync = sig !== self.appliedSig() && (codes.length || self.appliedSig() !== '');
        var syncStep = needSync ? self.syncDiscountCodes() : Promise.resolve(null);

        return Promise.resolve(syncStep).then(function () {
          if (pend.length || entryPend.length || needSync) {
            return self.refreshCartSections().then(function () { self.refreshCartCount(); });
          }
          return null;
        }).then(function () { return cart2; });
      }).then(function (cart2) {
        var state2 = self.patternsState(cart2);
        var entry2 = self.entryState(cart2);
        var offers = self.offersFor(state2);
        if (self.hasAnchor(state2) && offers.length) { self.renderCartPage(offers, state2.bundleInCart); return; }
        if (self.showEntry(state2, entry2)) self.renderEntryCartPage(entry2);
      });
    });
  }

  /* Dawn removes/changes line items via AJAX (no page reload), so watch
     the items area and re-evaluate the offer whenever the cart changes —
     e.g. removing the Playtime line brings its upsell row back. Our own
     block lives in the footer's page-width, outside the observed node,
     so re-rendering it can't retrigger the observer. */
  observeCartPage() {
    var target = document.getElementById('main-cart-items');
    if (!target) return;
    var self = this;
    new MutationObserver(function () {
      clearTimeout(self._cartPageTimer);
      self._cartPageTimer = setTimeout(function () { self.refreshCartPageUpsell(); }, 200);
    }).observe(target, { childList: true, subtree: true });
  }

  refreshCartPageUpsell() {
    var self = this;
    this.getCart().then(function (cart) {
      var existing = document.getElementById('sss-cart-upsell');

      if (!cart.items || !cart.items.length) {
        if (existing) existing.remove();
        return;
      }

      var state = self.patternsState(cart);
      var entry = self.entryState(cart);
      var offers = self.offersFor(state);

      /* keep discount codes + tracking attributes in sync when an add-on
         line was removed directly from the cart table (e.g. the x2 code
         must switch back to the single code) — and then RE-RENDER
         the totals, because Dawn already painted them before the new code
         was applied. The section refresh retriggers this observer once
         more, but the sig checks below make that second pass a no-op. */
      var sig = self.addonCodes(cart).slice().sort().join(',');
      var syncStep = (sig !== self.appliedSig())
        ? self.syncDiscountCodes()
            .then(function () { return self.refreshCartSections(); })
            .then(function () { self.refreshCartCount(); })
        : Promise.resolve();

      syncStep.then(function () {
        var current = document.getElementById('sss-cart-upsell');

        if (self.hasAnchor(state) && offers.length) {
          var offerSig = offers.map(function (p) { return p.key; }).join(',');
          if (current && current.dataset.sig === offerSig) return;
          self.renderCartPage(offers, state.bundleInCart);
          return;
        }

        if (self.showEntry(state, entry)) {
          var entrySig = 'entry:' + entry.kind;
          if (current && current.dataset.sig === entrySig) return;
          self.renderEntryCartPage(entry);
          return;
        }

        if (current) current.remove();
      });
    });
  }

  renderCartPage(offers, bundleInCart) {
    var self = this;
    Promise.all(offers.map(function (p) { return self.getProduct(p.handle); })).then(function (prods) {
      prods = prods.filter(Boolean);
      if (!prods.length) return;
      var block = self.buildCartBlock(offers.map(function (p) { return p.key; }).join(','));
      block.innerHTML = self.upsellMarkup(prods, 'cart', bundleInCart);
      self.placeCartBlock(block);
      self.bindBumps(block, 'cart');
    });
  }

  renderEntryCartPage(entry) {
    var self = this;
    this.getProduct(entry.target.handle).then(function (p) {
      if (!p) return;
      var block = self.buildCartBlock('entry:' + entry.kind);
      block.innerHTML = self.entryMarkup(p, entry.kind, 'cart');
      self.placeCartBlock(block);
      self.bindBumps(block, 'cart');
    });
  }

  buildCartBlock(sig) {
    var old = document.getElementById('sss-cart-upsell');
    if (old) old.remove();
    var block = document.createElement('div');
    block.id = 'sss-cart-upsell';
    block.className = 'sss-upsell-wrap';
    block.dataset.sig = sig;
    return block;
  }

  placeCartBlock(block) {
    var tries = 0;
    function attempt() {
      /* Sep 2026: put the upsell BELOW the Check out / Shop Pay / PayPal
         buttons, so nothing is pushed down when it loads. It sits outside
         the .js-contents blocks, so total refreshes never wipe it. */
      /* desktop: the empty left-hand column, beside the total + buttons */
      if (window.matchMedia && window.matchMedia('(min-width: 750px)').matches) {
        var foot = document.querySelector('#main-cart-footer .cart__footer');
        if (foot) { foot.insertBefore(block, foot.firstChild); return; }
      }
      /* mobile: under the buttons */
      var blocks = document.querySelector('#main-cart-footer .cart__blocks');
      if (blocks) {
        var errors = blocks.querySelector('#cart-errors');
        if (errors) blocks.insertBefore(block, errors); else blocks.appendChild(block);
        return;
      }
      // fallback: keep it INSIDE the page-width container so it isn't full browser width
      var pw = document.querySelector('#main-cart-items .page-width');
      if (pw) { pw.appendChild(block); return; }
      var footer = document.querySelector('#main-cart-footer .page-width');
      if (footer) { footer.insertBefore(block, footer.firstChild); return; }
      var footerSection = document.querySelector('#main-cart-footer');
      if (footerSection && footerSection.parentNode) { footerSection.parentNode.insertBefore(block, footerSection); return; }
      if (tries++ < 20) { setTimeout(attempt, 150); return; }
      document.body.appendChild(block);
    }
    attempt();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () { new CartUpsell(); });
} else {
  new CartUpsell();
}